import { Navigate, Outlet, RouteObject, createBrowserRouter } from "react-router-dom"
import { InternalAdminDashboardScreen } from "~/admin/admin-dashboard-screen"
import { ContactMessagesScreen } from "~/admin/contact-messages-screen"
import { InternalAdminLogsScreen } from "~/admin/logs-screen"
import { InternalAdminPuzzlesScreen } from "~/admin/puzzles-screen"
import {
  RequireSystemAdminSignedIn,
  RequireUserSignedIn,
  RequireUserSignedOut,
} from "~/auth/auth-layouts"
import { LoginLayout } from "~/auth/login-layout"
import {
  credentialsLoginPath,
  emailAuthPath,
  aboutPath,
  contactPath,
  dashboardPath,
  faqPath,
  internalAdminContactMessagesPath,
  internalAdminDashboardPath,
  internalAdminLogsPath,
  internalAdminPuzzlesPath,
  journeyLogEditPath,
  logJourneyPath,
  loginPath,
  profileEditPath,
  puzzleLabelPath,
  puzzleTrackingPath,
  registerPuzzlePath,
  rootPath,
  userPublicProfilePath,
} from "~/common/paths"
import { DashboardScreen } from "~/dashboard/dashboard-screen"
import { LandingScreen } from "~/landing/landing-screen"
import { AppLayout } from "~/layouts/app-layout"
import { CredentialsLoginScreen } from "~/login/credentials-login-screen"
import { EmailAuthScreen } from "~/login/email-auth-screen"
import { EmailLoginScreen } from "~/login/email-login-screen"
import { ProfileEditScreen } from "~/profile/profile-edit-screen"
import { PublicProfileScreen } from "~/profile/public-profile-screen"
import { JourneyLogEditScreen } from "~/puzzles/journey-log-edit-screen"
import { LogJourneyScreen } from "~/puzzles/log-journey-screen"
import { PuzzleLabelScreen } from "~/puzzles/puzzle-label-screen"
import { PuzzleTrackingScreen } from "~/puzzles/puzzle-tracking-screen"
import { RegisterPuzzleScreen } from "~/puzzles/register-puzzle-screen"
import { AboutScreen } from "~/static/about-screen"
import { ContactScreen } from "~/static/contact-screen"
import { FaqScreen } from "~/static/faq-screen"
import { ErrorBoundary } from "../ui/error-boundary"
import { RootLayout } from "./root-layout"

const systemAdminAuthenticatedRoutes: Array<RouteObject> = [
  {
    path: internalAdminDashboardPath.pattern,
    element: <InternalAdminDashboardScreen />,
  },
  {
    path: internalAdminContactMessagesPath.pattern,
    element: <ContactMessagesScreen />,
  },
  {
    path: internalAdminPuzzlesPath.pattern,
    element: <InternalAdminPuzzlesScreen />,
  },
  {
    path: internalAdminLogsPath.pattern,
    element: <InternalAdminLogsScreen />,
  },
]

const authenticatedRoutes: Array<RouteObject> = [
  {
    path: "/profile",
    element: <Navigate to={profileEditPath({})} replace />,
  },
  {
    path: profileEditPath.pattern,
    element: <ProfileEditScreen />,
  },
]

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <RequireSystemAdminSignedIn />,
        children: [
          {
            element: <AppLayout />,
            children: [
              {
                element: <Outlet />,
                children: [...systemAdminAuthenticatedRoutes],
              },
            ],
          },
        ],
      },
      {
        element: <RequireUserSignedOut />,
        children: [
          {
            element: <LoginLayout />,
            children: [
              {
                path: loginPath.pattern,
                element: <EmailLoginScreen />,
              },
              {
                path: credentialsLoginPath.pattern,
                element: <CredentialsLoginScreen />,
              },
              {
                path: emailAuthPath.pattern,
                element: <EmailAuthScreen />,
              },
            ],
          },
        ],
      },
      {
        path: rootPath.pattern,
        element: <LandingScreen />,
      },
      {
        path: puzzleTrackingPath.pattern,
        element: <PuzzleTrackingScreen />,
      },
      {
        path: userPublicProfilePath.pattern,
        element: <PublicProfileScreen />,
      },
      {
        path: contactPath.pattern,
        element: <ContactScreen />,
      },
      {
        path: aboutPath.pattern,
        element: <AboutScreen />,
      },
      {
        path: faqPath.pattern,
        element: <FaqScreen />,
      },
      {
        element: <RequireUserSignedIn />,
        children: [
          {
            element: <AppLayout />,
            children: [
              ...authenticatedRoutes,
              {
                path: logJourneyPath.pattern,
                element: <LogJourneyScreen />,
              },
              {
                path: journeyLogEditPath.pattern,
                element: <JourneyLogEditScreen />,
              },
              {
                path: registerPuzzlePath.pattern,
                element: <RegisterPuzzleScreen />,
              },
              {
                path: puzzleLabelPath.pattern,
                element: <PuzzleLabelScreen />,
              },
              {
                path: dashboardPath.pattern,
                element: <DashboardScreen />,
              },
            ],
          },
        ],
      },
    ],
  },
])
